import React, { useState, useEffect } from 'react';
import {
  FiActivity,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
  FiPlay,
  FiPause,
  FiTrash2,
  FiInfo,
  FiBarChart3,
  FiTrendingUp
} from 'react-icons/fi';
import api from '../services/api';

export default function JobQueueMonitor() {
  const [jobs, setJobs] = useState([]);
  const [queueStats, setQueueStats] = useState({
    waiting: 0,
    active: 0,
    completed: 0,
    failed: 0,
    delayed: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all', // all, waiting, active, completed, failed
    type: 'all' // all, import-questions-from-sheets, validate-import-data, etc.
  });

  // Job type configuration
  const jobTypes = {
    'import-questions-from-sheets': {
      name: 'Google Sheets Import',
      icon: FiActivity,
      color: '#3B82F6'
    },
    'import-questions-from-csv': {
      name: 'CSV Import',
      icon: FiActivity,
      color: '#8B5CF6'
    },
    'validate-import-data': {
      name: 'Data Validation',
      icon: FiCheckCircle,
      color: '#10B981'
    },
    'bulk-update-questions': {
      name: 'Bulk Update',
      icon: FiRefreshCw,
      color: '#F59E0B'
    }
  };

  // Fetch jobs from API
  const fetchJobs = async () => {
    try {
      setError('');
      const response = await api.get('/queue/dashboard', {
        params: {
          limit: 50,
          ...filters
        }
      });
      
      setJobs(response.data.data?.jobs || []);
      setQueueStats(response.data.data?.realTimeStats || queueStats);
      
    } catch (err) {
      setError('Failed to load job queue data');
      console.error('Error loading jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Job actions
  const handleRetryJob = async (jobId) => {
    try {
      await api.post(`/queue/jobs/${jobId}/retry`);
      await fetchJobs();
    } catch (err) {
      setError('Failed to retry job');
      console.error('Error retrying job:', err);
    }
  };

  const handleCancelJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to cancel this job?')) return;
    
    try {
      await api.delete(`/queue/jobs/${jobId}`);
      await fetchJobs();
    } catch (err) {
      setError('Failed to cancel job');
      console.error('Error canceling job:', err);
    }
  };

  const handleClearCompleted = async () => {
    if (!window.confirm('Clear all completed jobs? This cannot be undone.')) return;
    
    try {
      await api.delete('/queue/jobs/completed');
      await fetchJobs();
    } catch (err) {
      setError('Failed to clear completed jobs');
      console.error('Error clearing jobs:', err);
    }
  };

  // Auto-refresh effect
  useEffect(() => {
    fetchJobs();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchJobs, 5000); // Refresh every 5 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [filters, autoRefresh]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#10B981';
      case 'failed': return '#EF4444';
      case 'active': return '#3B82F6';
      case 'waiting': return '#F59E0B';
      case 'delayed': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return FiCheckCircle;
      case 'failed': return FiXCircle;
      case 'active': return FiPlay;
      case 'waiting': return FiClock;
      case 'delayed': return FiPause;
      default: return FiInfo;
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="job-queue-monitor">
      {/* Header with Controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '2rem'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#1F2937' }}>
            Job Queue Monitor
          </h2>
          <p style={{ margin: '0.5rem 0 0 0', color: '#6B7280', fontSize: '0.875rem' }}>
            Monitor and manage background job processing
          </p>
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          
          <button
            onClick={fetchJobs}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#F3F4F6',
              color: '#374151',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            <FiRefreshCw size={14} />
            Refresh
          </button>

          <button
            onClick={handleClearCompleted}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#FEF2F2',
              color: '#DC2626',
              border: '1px solid #FCA5A5',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            <FiTrash2 size={14} />
            Clear Completed
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          color: '#991B1B',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem'
        }}>
          {error}
        </div>
      )}

      {/* Queue Statistics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        {[
          { key: 'waiting', label: 'Waiting', color: '#F59E0B', icon: FiClock },
          { key: 'active', label: 'Active', color: '#3B82F6', icon: FiPlay },
          { key: 'completed', label: 'Completed', color: '#10B981', icon: FiCheckCircle },
          { key: 'failed', label: 'Failed', color: '#EF4444', icon: FiXCircle },
          { key: 'delayed', label: 'Delayed', color: '#8B5CF6', icon: FiPause }
        ].map(stat => {
          const Icon = stat.icon;
          return (
            <div key={stat.key} style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              textAlign: 'center'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '0.5rem'
              }}>
                <Icon size={20} style={{ color: stat.color }} />
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1F2937' }}>
                {queueStats[stat.key] || 0}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                {stat.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <div>
          <label style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem', display: 'block' }}>
            Status
          </label>
          <select
            value={filters.status}
            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem',
              backgroundColor: 'white'
            }}
          >
            <option value="all">All Status</option>
            <option value="waiting">Waiting</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="delayed">Delayed</option>
          </select>
        </div>

        <div>
          <label style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem', display: 'block' }}>
            Job Type
          </label>
          <select
            value={filters.type}
            onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            style={{
              padding: '0.5rem 0.75rem',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem',
              backgroundColor: 'white'
            }}
          >
            <option value="all">All Types</option>
            {Object.entries(jobTypes).map(([key, config]) => (
              <option key={key} value={key}>{config.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Jobs Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #E5E7EB',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6B7280' }}>
            <FiRefreshCw size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem' }} />
            <div>Loading jobs...</div>
          </div>
        ) : jobs.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6B7280' }}>
            <FiActivity size={48} style={{ marginBottom: '1rem', opacity: 0.3 }} />
            <div style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>No jobs found</div>
            <div style={{ fontSize: '0.875rem' }}>
              {filters.status !== 'all' || filters.type !== 'all' 
                ? 'Try adjusting your filters'
                : 'Jobs will appear here when processing starts'
              }
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#F9FAFB' }}>
                <tr>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                    Job
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                    Status
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                    Progress
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                    Duration
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                    Created
                  </th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job, index) => {
                  const StatusIcon = getStatusIcon(job.status);
                  const jobConfig = jobTypes[job.name] || { name: job.name, icon: FiActivity, color: '#6B7280' };
                  const JobIcon = jobConfig.icon;
                  
                  return (
                    <tr 
                      key={job.id} 
                      style={{ 
                        borderBottom: index < jobs.length - 1 ? '1px solid #F3F4F6' : 'none',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedJob(job)}
                    >
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <JobIcon size={16} style={{ color: jobConfig.color }} />
                          <div>
                            <div style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1F2937' }}>
                              {jobConfig.name}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                              ID: {job.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <StatusIcon size={16} style={{ color: getStatusColor(job.status) }} />
                          <span style={{ 
                            fontSize: '0.875rem',
                            color: getStatusColor(job.status),
                            fontWeight: '500',
                            textTransform: 'capitalize'
                          }}>
                            {job.status}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {job.progress !== undefined ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{
                              width: '60px',
                              height: '6px',
                              backgroundColor: '#F3F4F6',
                              borderRadius: '3px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${job.progress}%`,
                                height: '100%',
                                backgroundColor: getStatusColor(job.status),
                                transition: 'width 0.3s'
                              }} />
                            </div>
                            <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                              {job.progress}%
                            </span>
                          </div>
                        ) : '-'}
                      </td>
                      <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>
                        {formatDuration(job.processedOn && job.finishedOn 
                          ? job.finishedOn - job.processedOn 
                          : job.processedOn 
                            ? Date.now() - job.processedOn 
                            : null)}
                      </td>
                      <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6B7280' }}>
                        {formatTimestamp(job.timestamp)}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center' }}>
                          {job.status === 'failed' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRetryJob(job.id);
                              }}
                              style={{
                                padding: '0.25rem',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#10B981'
                              }}
                              title="Retry job"
                            >
                              <FiRefreshCw size={14} />
                            </button>
                          )}
                          {['waiting', 'active', 'delayed'].includes(job.status) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCancelJob(job.id);
                              }}
                              style={{
                                padding: '0.25rem',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#EF4444'
                              }}
                              title="Cancel job"
                            >
                              <FiXCircle size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Job Details Modal */}
      {selectedJob && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1F2937' }}>
                Job Details
              </h3>
              <button
                onClick={() => setSelectedJob(null)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#6B7280'
                }}
              >
                <FiXCircle size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Job Type</label>
                <div style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                  {jobTypes[selectedJob.name]?.name || selectedJob.name}
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Status</label>
                <div style={{ fontSize: '0.875rem', color: getStatusColor(selectedJob.status) }}>
                  {selectedJob.status}
                </div>
              </div>

              {selectedJob.data && (
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Data</label>
                  <pre style={{
                    fontSize: '0.75rem',
                    backgroundColor: '#F9FAFB',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    overflow: 'auto',
                    color: '#6B7280'
                  }}>
                    {JSON.stringify(selectedJob.data, null, 2)}
                  </pre>
                </div>
              )}

              {selectedJob.failedReason && (
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#DC2626' }}>Error</label>
                  <div style={{
                    fontSize: '0.875rem',
                    backgroundColor: '#FEF2F2',
                    color: '#DC2626',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: '1px solid #FCA5A5'
                  }}>
                    {selectedJob.failedReason}
                  </div>
                </div>
              )}

              {selectedJob.returnvalue && (
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Results</label>
                  <pre style={{
                    fontSize: '0.75rem',
                    backgroundColor: '#F0FDF4',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    overflow: 'auto',
                    color: '#166534',
                    border: '1px solid #BBF7D0'
                  }}>
                    {JSON.stringify(selectedJob.returnvalue, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add CSS animation for spinning refresh icon */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}